import type {
  HttpClient,
  IAuthService,
  TokenEndpointResponse,
  TokenResponse,
} from './types';

import ms from 'ms';
import { type AuthProviderProps, User, UserManager } from 'oidc-react';

import {
  getHttpErrorStatus,
  getUserIdFromJwt,
  isJwtExpired,
  parseJwtPayload,
} from './utils';

export type AuthServiceConfig = Required<
  Pick<AuthProviderProps, 'authority' | 'clientId' | 'redirectUri' | 'scope'>
> &
  Omit<
    AuthProviderProps,
    | 'authority'
    | 'clientId'
    | 'redirectUri'
    | 'scope'
    | 'onSignIn'
    | 'onSignOut'
  >;

/**
 * Parameters Keycloak appends to an authorization callback, including error
 * redirects. It rejects any redirect_uri that carries one of them with
 * "Invalid parameter: redirect_uri", so a URL holding a leftover callback
 * parameter (an expired session, a bookmark) would block login permanently.
 */
const OIDC_RESPONSE_PARAMS = [
  'code',
  'state',
  'session_state',
  'iss',
  'error',
  'error_description',
];

export class AuthService implements IAuthService {
  private readonly userManager: UserManager;
  private shouldReauthenticate = false;
  private lastRefreshWasSessionExpiry = false;
  private refreshTokensPromise?: Promise<string | undefined>;
  private readonly oidcConfig: AuthProviderProps;
  // Check if we're on an OIDC callback URL - used to disable autoSignIn to let callback processing complete
  private readonly isOidcCallback: boolean;

  constructor(
    private readonly config: AuthServiceConfig,
    private readonly httpClient: HttpClient,
  ) {
    // Extract idp hint from URL if present
    // Use idp param to pass kc_idp_hint to keycloak: e.g. app.vestigas.com?idp=strabag directly navigates to strabag login
    const url = new URL(globalThis.location.href);
    const idpHint = url.searchParams.get('idp');

    const extraQueryParams: Record<string, string> = idpHint
      ? { kc_idp_hint: idpHint }
      : {};

    this.isOidcCallback =
      url.searchParams.has('state') && url.searchParams.has('code');

    // Build OIDC config once in constructor with all callbacks
    this.oidcConfig = {
      ...config,
      extraQueryParams,
      onSignIn: (userData) => {
        this.saveTokens({
          access_token: userData?.access_token ?? '',
          id_token: userData?.id_token,
          refresh_token: userData?.refresh_token,
        });

        // Redirect to clean URL (remove OIDC response params)
        globalThis.location.href = this.stripOidcResponseParams(
          globalThis.location.href,
        );
      },
      onSignInError: (error) => {
        console.error('OIDC sign-in error:', error);
        // If state not found, redirect to clean URL to trigger fresh login
        if (error?.message?.includes('No matching state')) {
          globalThis.location.href = this.stripOidcResponseParams(
            globalThis.location.href,
          );
        }
      },
      onSignOut: () => {
        this.removeTokens();
      },
      redirectUri: this.stripOidcResponseParams(config.redirectUri),
    };

    this.userManager = new UserManager({
      authority: config.authority,
      client_id: config.clientId,
      redirect_uri: this.oidcConfig.redirectUri as string,
      scope: config.scope,
    });
  }

  getAccessToken(): string | undefined {
    return localStorage.getItem('accessToken') ?? undefined;
  }

  getIdToken(): string | undefined {
    return localStorage.getItem('idToken') ?? undefined;
  }

  getRefreshToken(): string | undefined {
    return localStorage.getItem('refreshToken') ?? undefined;
  }

  isAccessTokenValid(): boolean {
    const accessToken = this.getAccessToken();

    if (!accessToken) {
      return false;
    }

    try {
      const payload = this.getJwtPayload(accessToken);

      if (!payload?.exp || typeof payload.exp !== 'number') {
        return false;
      }

      const leeway = ms('30s'); // Clock skew tolerance
      return payload.exp * 1000 - leeway > Date.now();
    } catch {
      return false;
    }
  }

  isAccessTokenExpired(): boolean {
    return isJwtExpired(this.getAccessToken());
  }

  isRefreshTokenExpired(): boolean {
    return isJwtExpired(this.getRefreshToken());
  }

  getUserIdFromAccessToken(): string | undefined {
    return getUserIdFromJwt(this.getAccessToken());
  }

  getJwtPayload(jwt: string | undefined): Record<string, unknown> | undefined {
    return parseJwtPayload(jwt);
  }

  refreshTokens(): Promise<string | undefined> {
    this.refreshTokensPromise ??= this.performTokenRefresh().finally(() => {
      this.refreshTokensPromise = undefined;
    });

    return this.refreshTokensPromise;
  }

  /**
   * Attempts to silently restore the session from the refresh token in
   * localStorage, so that a browser restart (which wipes the sessionStorage
   * the OIDC user lives in) does not force a visible re-login.
   *
   * Returns true when a usable session is present afterwards. Returns false
   * on an OIDC callback URL, so the AuthProvider can complete the redirect.
   */
  async restoreSession(): Promise<boolean> {
    if (this.isOidcCallback) {
      return false;
    }

    const existingUser = await this.userManager.getUser().catch(() => null);

    if (existingUser && !existingUser.expired) {
      return true;
    }

    if (!this.getRefreshToken()) {
      return false;
    }

    const accessToken = await this.refreshTokens();

    return Boolean(accessToken);
  }

  /**
   * Whether the last refresh came back empty because the session is over, as
   * opposed to the refresh breaking or the tokens going missing with nothing
   * scheduled to recover them. Lets callers tell a routine logout apart from
   * an identity-provider outage and from a stuck signed-out tab.
   */
  wasLastRefreshSessionExpiry(): boolean {
    return this.lastRefreshWasSessionExpiry;
  }

  private async performTokenRefresh(): Promise<string | undefined> {
    try {
      const refreshToken = this.getRefreshToken();

      if (!refreshToken) {
        // Only an expiry when a re-authentication is already pending: that is
        // the retry loop below, which wipes the tokens before redirecting.
        // Tokens missing for any other reason (another tab logged out, a
        // storage write failed) leave the app signed out with nothing
        // scheduled to recover it, so that stays a reported failure.
        this.lastRefreshWasSessionExpiry = this.shouldReauthenticate;
        return;
      }

      const tokenUrl = new URL(
        'protocol/openid-connect/token',
        this.oidcConfig.authority,
      ).toString();

      const formData = new URLSearchParams({
        client_id: this.oidcConfig?.clientId ?? this.config.clientId,
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
      });

      const response = await this.httpClient.post<TokenEndpointResponse>(
        tokenUrl,
        formData,
        {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        },
      );

      if (!this.getRefreshToken()) {
        // The stored tokens were removed while this request was in flight, by
        // a logout or re-authentication in this tab or in another tab sharing
        // the storage. Saving the response would revive the session that was
        // just ended, so the result is dropped and reported as a failed
        // refresh.
        return;
      }

      const tokenData = response.data;

      this.saveTokens(tokenData);

      // Keep the OIDC user in sync so the AuthProvider accepts the session
      // without a redirect to Keycloak (best-effort).
      await this.storeOidcUser(tokenData).catch(() => undefined);

      // Cancel any pending re-authentication scheduled by a prior failed refresh
      this.shouldReauthenticate = false;
      this.lastRefreshWasSessionExpiry = false;

      return tokenData.access_token;
    } catch (error) {
      // Only a definitive rejection by the token endpoint means the session
      // is over. Transient failures (network, 5xx) keep the tokens so a
      // later 401 can retry the refresh.
      const status = getHttpErrorStatus(error);
      const isSessionOver = status === 400 || status === 401;

      this.lastRefreshWasSessionExpiry = isSessionOver;

      if (!isSessionOver || this.shouldReauthenticate) {
        return;
      }

      this.shouldReauthenticate = true;

      this.scheduleReauthenticate();
    }
  }

  /**
   * Re-authenticate after a delay (consumer can override this behavior).
   *
   * A rejected redirect is retried: by then the tokens are already wiped, so
   * every later refresh exits early on the missing refresh token and nothing
   * would reschedule — the app would sit signed out but not redirecting until
   * a manual reload. Retrying stops as soon as a redirect unloads the page.
   */
  private scheduleReauthenticate(): void {
    setTimeout(() => {
      if (!this.shouldReauthenticate) {
        return;
      }

      this.reauthenticate().catch((reauthError: unknown) => {
        console.error('Re-authentication failed:', reauthError);
        this.scheduleReauthenticate();
      });
    }, ms('10s'));
  }

  private buildOidcUser(tokenData: TokenEndpointResponse): User {
    const idToken = tokenData.id_token ?? this.getIdToken();
    const accessTokenPayload = parseJwtPayload(tokenData.access_token);

    return new User({
      access_token: tokenData.access_token,
      expires_at:
        typeof accessTokenPayload?.exp === 'number'
          ? accessTokenPayload.exp
          : undefined,
      id_token: idToken,
      profile: (parseJwtPayload(idToken) ?? {}) as User['profile'],
      refresh_token: tokenData.refresh_token,
      scope: tokenData.scope ?? this.config.scope,
      session_state: tokenData.session_state ?? null,
      token_type: tokenData.token_type ?? 'Bearer',
    });
  }

  private async storeOidcUser(tokenData: TokenEndpointResponse): Promise<void> {
    await this.userManager.storeUser(this.buildOidcUser(tokenData));
  }

  /**
   * The token endpoint definitively rejected the refresh token: drop the
   * local session and start a fresh authorization code flow. Unlike
   * logout(), this must NOT call Keycloak's end-session endpoint — while
   * the Keycloak SSO cookie is still valid the redirect completes without
   * showing a login page, and killing the SSO session here would force
   * every SSO user onto the login screen.
   */
  private async reauthenticate(): Promise<void> {
    this.removeTokens();
    await this.userManager.removeUser().catch(() => undefined);

    await this.userManager.signinRedirect({
      extraQueryParams: this.oidcConfig.extraQueryParams as Record<
        string,
        string
      >,
    });
  }

  async logout() {
    this.removeTokens();

    // Use OIDC logout
    await this.userManager.signoutRedirect({
      extraQueryParams: {
        client_id: this.oidcConfig.clientId ?? this.config.clientId,
      },
      post_logout_redirect_uri: this.buildLogoutRedirectUri(),
    });
  }

  private buildLogoutRedirectUri(): string {
    const redirectUri = this.oidcConfig.redirectUri ?? this.config.redirectUri;

    // Use globalThis.location.origin as base for relative URLs, or as fallback if no redirectUri configured
    const url = new URL(redirectUri ?? '/', globalThis.location.origin);

    // Use set() to add or replace the logout param, preserving existing params and hash
    url.searchParams.set('logout', 'true');

    return url.toString();
  }

  isLogoutUrl(url: string): boolean {
    return url.includes('logout=true');
  }

  isOidcRedirectUrl(url: string): boolean {
    return (
      url.includes('state') &&
      url.includes('session_state') &&
      url.includes('code')
    );
  }

  accessTokenContainsFicKey(): boolean {
    return Boolean(this.getJwtPayload(this.getAccessToken())?.fic);
  }

  /**
   * Returns the OIDC configuration for the AuthProvider.
   * @param autoSignIn - Optional override for autoSignIn behavior.
   *   If false, autoSignIn will be disabled.
   *   If true or omitted, autoSignIn will be enabled unless on an OIDC callback URL.
   */
  getOidcConfig(autoSignIn = true): AuthProviderProps {
    return {
      ...this.oidcConfig,
      // Disable autoSignIn if explicitly set to false OR if we're on an OIDC callback URL
      autoSignIn: autoSignIn && !this.isOidcCallback,
    };
  }

  private saveTokens(data: TokenResponse): void {
    if (!data) {
      return;
    }

    try {
      // Simple token storage - no case conversion
      if (data.access_token) {
        localStorage.setItem('accessToken', data.access_token);
      }

      if (data.refresh_token) {
        localStorage.setItem('refreshToken', data.refresh_token);
      }

      if (data.id_token) {
        localStorage.setItem('idToken', data.id_token);
      }
    } catch (error) {
      // Avoid partial persistence
      this.removeTokens();
    }
  }

  /**
   * Drops the stored tokens so that restoreSession() cannot revive the
   * session from a refresh token that outlived a logout. Callers that leave
   * the page for the identity provider's end-session endpoint must call this
   * before navigating: the sign-out hook of the auth provider only runs once
   * the redirect returns, which a full-page redirect never does.
   *
   * A token refresh that is still waiting on the token endpoint, in this tab
   * or in another one, checks for a stored refresh token before saving its
   * response, so removing the tokens also voids that late response.
   */
  removeTokens(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('idToken');
  }

  private stripOidcResponseParams(url: string | URL): string {
    let urlObj: URL;

    if (typeof url === 'string' || url instanceof URL) {
      if (url instanceof URL) {
        urlObj = new URL(url.toString());
      } else {
        urlObj = new URL(url);
      }
    } else {
      throw new TypeError('stripOidcResponseParams expects a string or URL');
    }

    for (const param of OIDC_RESPONSE_PARAMS) {
      urlObj.searchParams.delete(param);
    }

    return urlObj.toString();
  }
}
